import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, Status, type NotificationItem } from '../api/client'
import { invalidateReminders } from '../api/invalidate'
import './Notifications.css'

const VIEWS = ['agenda', 'calendar', 'vehicle', 'obligation', 'list'] as const
type View = (typeof VIEWS)[number]

const VIEW_LABEL: Record<View, string> = {
  agenda: 'Agenda',
  calendar: 'Calendar',
  vehicle: 'By vehicle',
  obligation: 'By obligation',
  list: 'List',
}

function day(iso?: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

/** Words a day count the way a person would say it. */
function describeDays(days: number | null | undefined): string {
  if (days == null) return 'no date recorded'
  if (days < 0) return `${-days} days overdue`
  if (days === 0) return 'today'
  if (days === 1) return 'tomorrow'
  return `in ${days} days`
}

/**
 * Four buckets and a fifth for the dateless.
 *
 * Ordered by what a person does about them, not by date: the overdue ones are
 * chased today, this week's are booked, the next thirty days are planned, and the
 * rest are read once. A single sorted list makes all four look alike.
 */
const BUCKETS = [
  { key: 'overdue', label: 'Overdue', match: (d: number | null) => d != null && d < 0 },
  { key: 'week', label: 'This week', match: (d: number | null) => d != null && d >= 0 && d <= 7 },
  { key: 'month', label: 'Next 30 days', match: (d: number | null) => d != null && d > 7 && d <= 30 },
  { key: 'later', label: 'Later', match: (d: number | null) => d != null && d > 30 },
  // Not "in 0 days". An unrecorded obligation has no date, and the four-state
  // model exists precisely to keep that distinct from a date of any kind.
  { key: 'none', label: 'No date recorded', match: (d: number | null) => d == null },
] as const

/**
 * The notification centre.
 *
 * ------------------------------------------------------------------
 * WHAT THIS LIST IS, AND IS NOT
 * ------------------------------------------------------------------
 * It is the ledger of warnings actually sent — not everything currently due. A
 * due item is computed: it has no identity, so it could not be cleared, could not
 * link to the message that carried it, and would change under the reader as dates
 * move. A ledger row is a fact with an id.
 *
 * The cost is real and worth naming: an obligation due but not yet warned about
 * is not here. The poller's interval is the lag, and the dashboard and register
 * are where "what is coming" is answered. This answers "what were we told".
 *
 * Each row carries the expiry the warning was ABOUT and what the register says
 * NOW, so a certificate renewed since the warning reads as resolved rather than
 * as an outstanding problem.
 */
export function Notifications() {
  const [params, setParams] = useSearchParams()
  const queryClient = useQueryClient()

  const raw = params.get('view')
  const view: View = VIEWS.includes(raw as View) ? (raw as View) : 'agenda'

  const [showCleared, setShowCleared] = useState(false)
  const [picked, setPicked] = useState<Set<number>>(new Set())

  const feed = useQuery({
    queryKey: ['notifications', showCleared],
    queryFn: () => api.getNotifications(showCleared, 1, 500),
    placeholderData: keepPreviousData,
  })

  const items = useMemo(() => feed.data?.items ?? [], [feed.data])

  const dismiss = useMutation({
    mutationFn: (ids: number[]) => api.dismissNotifications(ids),
    onSuccess: async () => {
      setPicked(new Set())
      await invalidateReminders(queryClient)
    },
  })

  const restore = useMutation({
    mutationFn: (ids: number[]) => api.restoreNotifications(ids),
    onSuccess: async () => {
      setPicked(new Set())
      await invalidateReminders(queryClient)
    },
  })

  function setView(next: View) {
    // In the URL, like the register's filters: Back restores the view and a view
    // is a shareable link.
    const copy = new URLSearchParams(params)
    copy.set('view', next)
    setParams(copy, { replace: true })
  }

  function toggle(id: number) {
    setPicked((was) => {
      const next = new Set(was)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const clearedCount = items.filter((i) => i.dismissedAtUtc).length

  return (
    <div className="notif">
      <header className="notif__head">
        <div>
          <h1>Notifications</h1>
          <p className="notif__sub">
            What has been warned about. {items.length}
            {items.length === 1 ? ' item' : ' items'}
            {showCleared && clearedCount > 0 && `, ${clearedCount} cleared`}
          </p>
        </div>

        <div className="notif__actions">
          <label className="notif__toggle">
            <input
              type="checkbox"
              checked={showCleared}
              onChange={(e) => setShowCleared(e.target.checked)}
            />
            Show cleared
          </label>

          {/* Manage rules, not manage notifications: a rule is set up once and
              left alone, a notification is read daily. The daily thing gets the
              header slot and the rules screen is one click away from it. */}
          <Link className="notif__link" to="/reminders">
            Manage rules
          </Link>

          <button
            type="button"
            className="btn btn--clear"
            disabled={dismiss.isPending || items.every((i) => i.dismissedAtUtc)}
            onClick={() => dismiss.mutate(picked.size > 0 ? [...picked] : [])}
            title={
              picked.size > 0
                ? `Clear the ${picked.size} selected`
                : 'Clear everything from your own bell'
            }
          >
            {picked.size > 0 ? `Clear ${picked.size}` : 'Clear all'}
          </button>
        </div>
      </header>

      {/*
        Clearing is per person and never touches the record of what was sent.
        Said once, here, rather than in a tooltip nobody opens: somebody about to
        press "Clear all" is entitled to know it is not destructive.
      */}
      <p className="notif__privacy">
        Clearing affects only your own list. The record of what was sent is kept.
      </p>

      <nav className="seg notif__views" aria-label="View">
        {VIEWS.map((v) => (
          <button
            key={v}
            type="button"
            aria-pressed={view === v}
            onClick={() => setView(v)}
          >
            {VIEW_LABEL[v]}
          </button>
        ))}
      </nav>

      {feed.isPending && <p className="muted">Loading…</p>}
      {feed.isError && <p className="banner banner--error">Could not load notifications.</p>}

      {feed.data && items.length === 0 && (
        <p className="notif__empty">
          Nothing here. Either nothing has been warned about yet, or you have cleared it
          all — tick <b>Show cleared</b> to tell the two apart.
        </p>
      )}

      {items.length > 0 && (
        <>
          {view === 'agenda' && <Agenda items={items} onClear={(id) => dismiss.mutate([id])} />}
          {view === 'calendar' && <Calendar items={items} />}
          {view === 'vehicle' && (
            <Grouped
              items={items}
              by={(i) => i.registration ?? '—'}
              caption={(i) => [i.vehicleTypeName, i.siteName].filter(Boolean).join(' · ')}
              onClear={(id) => dismiss.mutate([id])}
            />
          )}
          {view === 'obligation' && (
            <Grouped
              items={items}
              by={(i) => i.complianceTypeName ?? '—'}
              caption={() => ''}
              onClear={(id) => dismiss.mutate([id])}
            />
          )}
          {view === 'list' && (
            <FlatList
              items={items}
              picked={picked}
              onToggle={toggle}
              onPickAll={() =>
                setPicked((was) =>
                  was.size === items.length
                    ? new Set()
                    : new Set(items.map((i) => i.reminderSentId!)),
                )
              }
              onRestore={(id) => restore.mutate([id])}
            />
          )}
        </>
      )}
    </div>
  )
}

/** Severity of one row, from what the register says NOW rather than then. */
function toneOf(item: NotificationItem): string {
  if (item.currentStatus === Status.Known && item.currentExpiryDate) {
    const d = item.daysUntilExpiry
    if (d != null && d < 0) return 'crit'
    if (d != null && d <= 7) return 'warn'
    return 'ok'
  }
  return 'none'
}

/** One row, shared by every view that shows rows. */
function Row({
  item,
  onClear,
}: {
  item: NotificationItem
  onClear?: (id: number) => void
}) {
  // The warning was about one date; the register may have moved on since. Saying
  // both is the difference between "still a problem" and "somebody fixed it".
  const renewed =
    item.remindedExpiryDate &&
    item.currentExpiryDate &&
    item.currentExpiryDate > item.remindedExpiryDate

  return (
    <li className={`nrow nrow--${toneOf(item)}${item.dismissedAtUtc ? ' nrow--cleared' : ''}`}>
      <i className="nrow__stripe" aria-hidden="true" />

      <div className="nrow__main">
        <div className="nrow__top">
          <Link className="nrow__reg mono" to={`/vehicles/${item.registration}`}>
            {item.registration}
          </Link>
          <span className="nrow__ob">{item.complianceTypeName}</span>
          {renewed && (
            <span className="nrow__renewed" title="Renewed after the warning went out">
              renewed
            </span>
          )}
        </div>

        <div className="nrow__meta">
          <span>{describeDays(item.daysUntilExpiry)}</span>
          <span>expires {day(item.currentExpiryDate)}</span>
          {renewed && <span>warned about {day(item.remindedExpiryDate)}</span>}
          <span>{[item.vehicleTypeName, item.siteName].filter(Boolean).join(' · ')}</span>
        </div>

        <div className="nrow__meta nrow__meta--quiet">
          <span>{item.ruleName}</span>
          <span>warned {day(item.sentAtUtc)}</span>
          {/* 3 is Failed. A warning nobody received is worse than none, because
              everybody believes it went out. */}
          {item.deliveryStatus === 3 && <span className="nrow__failed">delivery failed</span>}
        </div>
      </div>

      {onClear && !item.dismissedAtUtc && (
        <button
          type="button"
          className="nrow__clear"
          onClick={() => onClear(item.reminderSentId!)}
          aria-label={`Clear the ${item.complianceTypeName} warning for ${item.registration}`}
          title="Clear from your list"
        >
          ×
        </button>
      )}
    </li>
  )
}

/** Overdue first, then what is bookable, then what is merely known. */
function Agenda({
  items,
  onClear,
}: {
  items: NotificationItem[]
  onClear: (id: number) => void
}) {
  return (
    <div className="notif__band">
      {BUCKETS.map((bucket) => {
        const rows = items.filter((i) => bucket.match(i.daysUntilExpiry ?? null))
        if (rows.length === 0) return null

        return (
          <section key={bucket.key} className={`nbucket nbucket--${bucket.key}`}>
            <h3>
              {bucket.label}
              <span className="nbucket__count">{rows.length}</span>
            </h3>
            <ul className="nlist">
              {rows.map((i) => (
                <Row key={i.reminderSentId} item={i} onClear={onClear} />
              ))}
            </ul>
          </section>
        )
      })}
    </div>
  )
}

/** Anything grouped by a key the caller chooses. */
function Grouped({
  items,
  by,
  caption,
  onClear,
}: {
  items: NotificationItem[]
  by: (i: NotificationItem) => string
  caption: (i: NotificationItem) => string
  onClear: (id: number) => void
}) {
  const groups = useMemo(() => {
    const map = new Map<string, NotificationItem[]>()
    for (const i of items) {
      const key = by(i)
      const list = map.get(key)
      if (list) list.push(i)
      else map.set(key, [i])
    }
    // Worst first: a group holding something overdue is the one to open.
    return [...map.entries()].sort((a, b) => {
      const worst = (rows: NotificationItem[]) =>
        Math.min(...rows.map((r) => r.daysUntilExpiry ?? 9999))
      return worst(a[1]) - worst(b[1])
    })
  }, [items, by])

  return (
    <div className="notif__band">
      {groups.map(([key, rows]) => (
        <section key={key} className="nbucket">
          <h3>
            <span className="mono">{key}</span>
            <span className="nbucket__cap">{caption(rows[0])}</span>
            <span className="nbucket__count">{rows.length}</span>
          </h3>
          <ul className="nlist">
            {rows.map((i) => (
              <Row key={i.reminderSentId} item={i} onClear={onClear} />
            ))}
          </ul>
        </section>
      ))}
    </div>
  )
}

/**
 * A month grid over the expiry dates.
 *
 * Deliberately keyed on the CURRENT expiry, not the date the warning was sent.
 * A calendar answers "when does this fall due"; the send date is provenance and
 * belongs on the row.
 */
function Calendar({ items }: { items: NotificationItem[] }) {
  /*
    Opens on the month the data is actually in, not on today.

    Every warning in the feed has already been sent, so its expiry is typically
    weeks or months out — and on this fleet the current month is routinely empty.
    Opening there showed a blank grid and read as a broken feature, which is
    exactly what happened.

    Today's month wins when it has something, since that is where a reader
    expects to land; otherwise the calendar goes to the earliest month that has
    anything to show.
  */
  const firstMonthWithItems = useMemo(() => {
    const now = new Date()
    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1)

    const dates = items
      .map((i) => i.currentExpiryDate)
      .filter((d): d is string => Boolean(d))
      .sort()

    if (dates.length === 0) return thisMonth

    const hasThisMonth = dates.some((d) => {
      const dt = new Date(d)
      return dt.getFullYear() === now.getFullYear() && dt.getMonth() === now.getMonth()
    })

    if (hasThisMonth) return thisMonth

    const earliest = new Date(dates[0])
    return new Date(earliest.getFullYear(), earliest.getMonth(), 1)
  }, [items])

  const [monthStart, setMonthStart] = useState(firstMonthWithItems)

  const byDate = useMemo(() => {
    const map = new Map<string, NotificationItem[]>()
    for (const i of items) {
      if (!i.currentExpiryDate) continue
      const key = i.currentExpiryDate.slice(0, 10)
      const list = map.get(key)
      if (list) list.push(i)
      else map.set(key, [i])
    }
    return map
  }, [items])

  const year = monthStart.getFullYear()
  const month = monthStart.getMonth()
  const daysInMonth = new Date(year, month + 1, 0).getDate()

  // Monday-first, which is how a working week is read here.
  const firstWeekday = (new Date(year, month, 1).getDay() + 6) % 7

  const cells: (number | null)[] = [
    ...Array.from({ length: firstWeekday }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ]

  const undated = items.filter((i) => !i.currentExpiryDate)

  const countIn = (m: Date) =>
    items.filter((i) => {
      if (!i.currentExpiryDate) return false
      const d = new Date(i.currentExpiryDate)
      return d.getFullYear() === m.getFullYear() && d.getMonth() === m.getMonth()
    }).length

  const monthCount = countIn(monthStart)

  // The nearest month after this one that has anything, so an empty month can
  // offer a way forward rather than leaving the reader to page through blanks.
  const nextMonthWithItems = useMemo(() => {
    const after = items
      .map((i) => i.currentExpiryDate)
      .filter((d): d is string => Boolean(d))
      .map((d) => new Date(d))
      .filter((d) => new Date(d.getFullYear(), d.getMonth(), 1) > monthStart)
      .sort((a, b) => a.getTime() - b.getTime())[0]

    return after ? new Date(after.getFullYear(), after.getMonth(), 1) : null
  }, [items, monthStart])

  return (
    <div className="ncal">
      <div className="ncal__head">
        <button
          type="button"
          onClick={() => setMonthStart(new Date(year, month - 1, 1))}
          aria-label="Previous month"
        >
          ‹
        </button>
        <b>
          {monthStart.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
        </b>
        <button
          type="button"
          onClick={() => setMonthStart(new Date(year, month + 1, 1))}
          aria-label="Next month"
        >
          ›
        </button>
      </div>

      <div className="ncal__grid">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
          <div key={d} className="ncal__dow">
            {d}
          </div>
        ))}

        {cells.map((d, idx) => {
          if (d == null) return <div key={`pad-${idx}`} className="ncal__cell ncal__cell--pad" />

          const key = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
          const rows = byDate.get(key) ?? []
          const worst = rows.length
            ? Math.min(...rows.map((r) => r.daysUntilExpiry ?? 9999))
            : null

          return (
            <div
              key={key}
              className={`ncal__cell${rows.length ? ` ncal__cell--has ncal__cell--${
                worst != null && worst < 0 ? 'crit' : worst != null && worst <= 7 ? 'warn' : 'ok'
              }` : ''}`}
            >
              <span className="ncal__day">{d}</span>
              {rows.slice(0, 3).map((r) => (
                <Link
                  key={r.reminderSentId}
                  className="ncal__pill mono"
                  to={`/vehicles/${r.registration}`}
                  title={`${r.registration} · ${r.complianceTypeName}`}
                >
                  {r.registration}
                </Link>
              ))}
              {rows.length > 3 && <span className="ncal__more">+{rows.length - 3} more</span>}
            </div>
          )
        })}
      </div>

      {/*
        An empty month says so, and says where the items ARE.

        A blank grid is indistinguishable from a broken feature — and on this
        fleet most months genuinely are empty, so this is the common case rather
        than an edge one.
      */}
      {monthCount === 0 && (
        <p className="ncal__undated">
          Nothing falls due in{' '}
          {monthStart.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}.
          {nextMonthWithItems && (
            <>
              {' '}
              <button
                type="button"
                className="ncal__jump"
                onClick={() => setMonthStart(nextMonthWithItems)}
              >
                Go to{' '}
                {nextMonthWithItems.toLocaleDateString(undefined, {
                  month: 'long',
                  year: 'numeric',
                })}
                , which has {countIn(nextMonthWithItems)} ›
              </button>
            </>
          )}
        </p>
      )}

      {/*
        A calendar cannot show something with no date, and silently dropping it
        would make the view disagree with every other one on the page.
      */}
      {undated.length > 0 && (
        <p className="ncal__undated">
          {undated.length} {undated.length === 1 ? 'warning has' : 'warnings have'} no recorded
          expiry and cannot appear on a calendar. The agenda lists them.
        </p>
      )}
    </div>
  )
}

/** The flat one, with checkboxes — this is what makes bulk clearing real. */
function FlatList({
  items,
  picked,
  onToggle,
  onPickAll,
  onRestore,
}: {
  items: NotificationItem[]
  picked: Set<number>
  onToggle: (id: number) => void
  onPickAll: () => void
  onRestore: (id: number) => void
}) {
  return (
    <div className="nflat">
      <label className="nflat__all">
        <input
          type="checkbox"
          checked={picked.size > 0 && picked.size === items.length}
          ref={(el) => {
            // Partly selected is its own state: an unchecked box on a page where
            // six of forty are picked would read as "nothing chosen".
            if (el) el.indeterminate = picked.size > 0 && picked.size < items.length
          }}
          onChange={onPickAll}
        />
        Select all {items.length}
      </label>

      <ul className="nlist">
        {items.map((i) => (
          <li
            key={i.reminderSentId}
            className={`nrow nrow--${toneOf(i)}${i.dismissedAtUtc ? ' nrow--cleared' : ''}`}
          >
            <input
              type="checkbox"
              className="nrow__pick"
              checked={picked.has(i.reminderSentId!)}
              onChange={() => onToggle(i.reminderSentId!)}
              aria-label={`Select ${i.registration} ${i.complianceTypeName}`}
            />
            <i className="nrow__stripe" aria-hidden="true" />

            <div className="nrow__main">
              <div className="nrow__top">
                <Link className="nrow__reg mono" to={`/vehicles/${i.registration}`}>
                  {i.registration}
                </Link>
                <span className="nrow__ob">{i.complianceTypeName}</span>
              </div>
              <div className="nrow__meta">
                <span>{describeDays(i.daysUntilExpiry)}</span>
                <span>expires {day(i.currentExpiryDate)}</span>
                <span>{i.ruleName}</span>
                <span>warned {day(i.sentAtUtc)}</span>
              </div>
            </div>

            {i.dismissedAtUtc && (
              <button
                type="button"
                className="nrow__clear"
                onClick={() => onRestore(i.reminderSentId!)}
                title="Put this back on your list"
              >
                undo
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}
